/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import {Millisecond} from '../units/Units';
import GlobalScope from '../globalScope/GlobalScope';
import Disposable from '../lang/Disposable';
import DisposableList from '../lang/DisposableList';
import Subject from '../rx/Subject';
import ReadOnlySubject from '../rx/ReadOnlySubject';

const visibilityChangeEvent = 'visibilitychange';

export default class ApplicationActivityMonitor {
  private readonly _disposables: DisposableList = new DisposableList();
  private readonly _readOnlyIsForeground: ReadOnlySubject<boolean>;
  private readonly _isForeground: Subject<boolean>;
  private readonly _timeOfLastTabFocusChange: number = Date.now();
  private _lastBackgroundDuration: Millisecond | null = null;

  constructor() {
    this._isForeground = new Subject<boolean>(true);
    this._readOnlyIsForeground = new ReadOnlySubject(this._isForeground);
    this.initialize();
  }

  get isForeground(): ReadOnlySubject<boolean> {
    return this._readOnlyIsForeground;
  }

  public getTimeSinceLastChange(): number {
    return Date.now() - this._timeOfLastTabFocusChange;
  }

  public getLastBackgroundDuration(): Millisecond | null {
    return this._lastBackgroundDuration;
  }

  public dispose(): void {
    this._disposables.dispose();
  }

  private initialize(): void {
    let backgroundEntryTime: Millisecond | null = null;
    const handleVisibilityChange = () => {
      const isNowForeground = !GlobalScope.value.documentIsHidden;

      if (!isNowForeground) {
        backgroundEntryTime = Date.now();
        this._lastBackgroundDuration = null;
      } else if (backgroundEntryTime) {
        this._lastBackgroundDuration = Date.now() - backgroundEntryTime;
      }

      this._isForeground.value = isNowForeground;
    };

    const useEventCapturePhase = false;

    GlobalScope.value.addDocumentEventListener(visibilityChangeEvent, handleVisibilityChange, useEventCapturePhase);

    this._disposables.add(new Disposable(() => GlobalScope.value.removeDocumentEventListener(visibilityChangeEvent, handleVisibilityChange, useEventCapturePhase)));
  }
}